import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Toaster } from 'react-hot-toast';
import { Box, CircularProgress, Typography, Button, Tooltip, Zoom } from '@mui/material';
import { Home as HomeIcon, WhatsApp } from '@mui/icons-material';
import { Link } from 'react-router-dom';

// ─── Layouts ──────────────────────────────────────────────────────────────────
import MainLayout  from './components/Layout/MainLayout';
import AdminLayout from './components/Layout/AdminLayout';

// ─── Public Pages ─────────────────────────────────────────────────────────────
import Home        from './pages/Home';
import Fruits      from './pages/Fruits';
import FruitDetail from './pages/FruitDetail';
import HowItWorks  from './pages/HowItWorks';
import About       from './pages/About';
import Contact     from './pages/Contact';

// ─── Auth Pages ───────────────────────────────────────────────────────────────
import Login           from './pages/Auth/Login';
import Register        from './pages/Auth/Register';
import ForgotPassword  from './pages/Auth/ForgotPassword';
import ResetPassword   from './pages/Auth/ResetPassword';
import GoogleCallback  from './pages/Auth/GoogleCallback';
import AuthCallback    from './pages/Auth/AuthCallback';
import SelectRole      from './pages/Auth/SelectRole';

// ─── Buyer Pages ──────────────────────────────────────────────────────────────
import BuyerDashboard    from './pages/Buyer/Dashboard';
import BuyerEnquiries    from './pages/Buyer/Enquiries';
import BuyerInvoices     from './pages/Buyer/Invoices';
import BuyerVerification from './pages/Buyer/Verification';
import BuyerPayment      from './pages/Buyer/Payment';
import BuyerShipping     from './pages/Buyer/Shipping';
import BuyerMessages     from './pages/Buyer/Messages';

// ─── Seller Pages ─────────────────────────────────────────────────────────────
import SellerDashboard     from './pages/Seller/Dashboard';
import SellerProducts      from './pages/Seller/Products';
import SellerOrders        from './pages/Seller/order';
import SellerShipments     from './pages/Seller/Shipments';
import SellerInvoices      from './pages/Seller/Invoices';
import SellerVerification  from './pages/Seller/Verification';
import SellerMessages      from './pages/Seller/Messages';

// ─── Shared Pages ─────────────────────────────────────────────────────────────
import Profile  from './pages/Shared/Profile';
import Settings from './pages/Shared/Settings';
import ChatPage from './pages/Shared/Chat';

// ─── Admin Pages ──────────────────────────────────────────────────────────────
import AdminDashboard     from './pages/Admin/Dashboard';
import AdminFruits        from './pages/Admin/Fruits';
import AdminAddFruit      from './pages/Admin/AddFruit';
import AdminEditFruit     from './pages/Admin/EditFruit';
import AdminEnquiries     from './pages/Admin/Enquiries';
import AdminPayments      from './pages/Admin/Payments';
import AdminVerifications from './pages/Admin/Verifications';
import AdminShipments     from './pages/Admin/Shipments';
import AdminUsers         from './pages/Admin/Users';

// ─── Auth Context ─────────────────────────────────────────────────────────────
import { AuthProvider, useAuth } from './context/AuthContext';

// ─── Scroll To Top on route change ───────────────────────────────────────────
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
};

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#000000',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#666666',
      disabled: '#999999',
    },
  },
  typography: {
    fontFamily: [
      'Inter', '-apple-system', 'BlinkMacSystemFont',
      '"Segoe UI"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif',
    ].join(','),
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: { borderRadius: 12 },
});

// ─── Loading Screen ───────────────────────────────────────────────────────────
const LoadingScreen = () => (
  <Box sx={{
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    minHeight: '100vh', backgroundColor: 'background.default',
  }}>
    <Box sx={{ textAlign: 'center' }}>
      <CircularProgress size={60} thickness={4} />
      <Box sx={{ mt: 3 }}>
        <Typography variant="h5"
          sx={{ fontWeight: 600, color: 'primary.main', letterSpacing: '0.05em' }}>
          SMARTWAVE INTERNATIONAL
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Loading...
        </Typography>
      </Box>
    </Box>
  </Box>
);

// ─── 404 Page ─────────────────────────────────────────────────────────────────
const NotFoundPage = () => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '70vh', textAlign: 'center', px: 2,
  }}>
    <Box sx={{
      fontSize: '8rem', fontWeight: 800, color: 'primary.light',
      lineHeight: 1, mb: 2, opacity: 0.1,
    }}>
      404
    </Box>
    <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
      Page Not Found
    </Typography>
    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: '500px' }}>
      The page you are looking for doesn't exist or has been moved.
    </Typography>
    <Button component={Link} to="/" variant="contained" size="large"
      startIcon={<HomeIcon />} sx={{ px: 4, py: 1.5, fontSize: '1rem' }}>
      Back to Homepage
    </Button>
  </Box>
);

// ─── Protected Route ──────────────────────────────────────────────────────────
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { loading } = useAuth();

  if (loading) return <LoadingScreen />;

  const token      = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');

  if (!token || !storedUser) {
    return <Navigate to="/login" replace />;
  }

  let userData;
  try {
    userData = JSON.parse(storedUser);
  } catch {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return <Navigate to="/login" replace />;
  }

  if (!userData.user_type && window.location.pathname !== '/auth/select-role') {
    return <Navigate to="/auth/select-role" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(userData.user_type)) {
    switch (userData.user_type) {
      case 'admin':  return <Navigate to="/admin/dashboard"  replace />;
      case 'seller': return <Navigate to="/seller/dashboard" replace />;
      case 'buyer':  return <Navigate to="/buyer/dashboard"  replace />;
      default:       return <Navigate to="/login"            replace />;
    }
  }

  return children;
};

// ─── Public Route ─────────────────────────────────────────────────────────────
const PublicRoute = ({ children }) => {
  const { loading } = useAuth();

  if (loading) return <LoadingScreen />;

  const token      = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');

  if (token && storedUser) {
    try {
      const userData = JSON.parse(storedUser);

      if (!userData.user_type) {
        return <Navigate to="/auth/select-role" replace />;
      }

      switch (userData.user_type) {
        case 'admin':  return <Navigate to="/admin/dashboard"  replace />;
        case 'seller': return <Navigate to="/seller/dashboard" replace />;
        case 'buyer':  return <Navigate to="/buyer/dashboard"  replace />;
        default:       return children;
      }
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return children;
    }
  }

  return children;
};

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Routes>

            {/* ══════════════════════════════════
                PUBLIC ROUTES
            ══════════════════════════════════ */}
            <Route path="/" element={<MainLayout />}>
              <Route index element={<Home />} />
              <Route path="fruits"       element={<Fruits />} />
              <Route path="fruits/:id"   element={<FruitDetail />} />
              <Route path="how-it-works" element={<HowItWorks />} />
              <Route path="about"        element={<About />} />
              <Route path="contact"      element={<Contact />} />
              <Route path="login"           element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="register"        element={<PublicRoute><Register /></PublicRoute>} />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="reset-password"  element={<ResetPassword />} />
              {/* Chat route — accessible by buyer, seller, admin */}
              <Route path="chat/:enquiryId" element={<ChatPage />} />
            </Route>


            {/* ══════════════════════════════════
                OAUTH CALLBACK ROUTES
            ══════════════════════════════════ */}
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/auth/callback"        element={<AuthCallback />} />

            {/* ══════════════════════════════════
                ROLE SELECTION
            ══════════════════════════════════ */}
            <Route
              path="/auth/select-role"
              element={
                <ProtectedRoute allowedRoles={[]}>
                  <SelectRole />
                </ProtectedRoute>
              }
            />

            {/* ══════════════════════════════════
                BUYER ROUTES
            ══════════════════════════════════ */}
            <Route
              path="/buyer"
              element={
                <ProtectedRoute allowedRoles={['buyer']}>
                  <MainLayout showBuyerNav={true} />
                </ProtectedRoute>
              }
            >
              <Route index                      element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard"           element={<BuyerDashboard />} />
              <Route path="enquiries"           element={<BuyerEnquiries />} />
              <Route path="enquiries/:id"       element={<BuyerEnquiries />} />
              <Route path="invoices"            element={<BuyerInvoices />} />
              <Route path="invoices/:id"        element={<BuyerInvoices />} />
              <Route path="verification"        element={<BuyerVerification />} />
              <Route path="payment/:enquiryId"  element={<BuyerPayment />} />
              <Route path="shipments"           element={<BuyerShipping />} />
              <Route path="shipping/:enquiryId" element={<BuyerShipping />} />
              <Route path="messages"            element={<BuyerMessages />} />
              <Route path="profile"             element={<Profile />} />
              <Route path="settings"            element={<Settings />} />
            </Route>

            {/* ══════════════════════════════════
                SELLER ROUTES
            ══════════════════════════════════ */}
            <Route
              path="/seller"
              element={
                <ProtectedRoute allowedRoles={['seller']}>
                  <MainLayout showSellerNav={true} />
                </ProtectedRoute>
              }
            >
              <Route index            element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard"    element={<SellerDashboard />} />
              <Route path="products"     element={<SellerProducts />} />
              <Route path="orders"       element={<SellerOrders />} />
              <Route path="shipments"    element={<SellerShipments />} />
              <Route path="invoices"     element={<SellerInvoices />} />
              <Route path="verification" element={<SellerVerification />} />
              <Route path="messages"     element={<SellerMessages />} />
              <Route path="profile"      element={<Profile />} />
              <Route path="settings"     element={<Settings />} />
            </Route>

            {/* ══════════════════════════════════
                ADMIN ROUTES
            ══════════════════════════════════ */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard"         element={<AdminDashboard />} />
              <Route path="fruits"            element={<AdminFruits />} />
              <Route path="fruits/add"        element={<AdminAddFruit />} />
              <Route path="fruits/edit/:id"   element={<AdminEditFruit />} />
              <Route path="enquiries"         element={<AdminEnquiries />} />
              <Route path="enquiries/:id"     element={<AdminEnquiries />} />
              <Route path="payments"          element={<AdminPayments />} />
              <Route path="verifications"     element={<AdminVerifications />} />
              <Route path="shipments"         element={<AdminShipments />} />
              <Route path="users"             element={<AdminUsers />} />
              <Route path="profile"           element={<Profile />} />
              <Route path="settings"          element={<Settings />} />
            </Route>

            {/* ══════════════════════════════════
                404 FALLBACK
            ══════════════════════════════════ */}
            <Route
              path="*"
              element={
                <MainLayout>
                  <NotFoundPage />
                </MainLayout>
              }
            />

          </Routes>
        </Router>
      </AuthProvider>

      {/* ── Floating WhatsApp Button (global) ─────────────────────────── */}
      <Tooltip title="Chat with us on WhatsApp" placement="left" TransitionComponent={Zoom}>
        <Box
          component="a"
          href="https://wa.me/918925851195?text=Hello%2C%20I%20am%20interested%20in%20your%20fruit%20export%20services."
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          sx={{
            position: 'fixed',
            bottom: 28,
            right: 28,
            zIndex: 9999,
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: '#25d366',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(37,211,102,0.5)',
            textDecoration: 'none',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'scale(1.12)',
              boxShadow: '0 6px 28px rgba(37,211,102,0.7)',
              bgcolor: '#20c45a',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '3px solid #25d366',
              animation: 'wa-pulse 2s ease-out infinite',
              opacity: 0,
            },
            '@keyframes wa-pulse': {
              '0%':   { transform: 'scale(1)',    opacity: 0.8 },
              '100%': { transform: 'scale(1.6)',  opacity: 0 },
            },
          }}
        >
          <WhatsApp sx={{ fontSize: 32 }} />
        </Box>
      </Tooltip>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: '#1a1a1a',
            color: '#ffffff',
            borderRadius: '12px',
            fontSize: '0.9375rem',
            maxWidth: '450px',
            padding: '16px 20px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          },
          success: {
            style: { background: '#2e7d32', borderLeft: '4px solid #1b5e20' },
          },
          error: {
            style: { background: '#d32f2f', borderLeft: '4px solid #b71c1c' },
          },
        }}
      />
    </ThemeProvider>
  );
}

export default App;